/**
 * In-Browser Pure Vector EPS 10 Exporter.
 * Converts SVG Bezier path data into clean, compliant PostScript Level 2 / EPS 10
 * format for Adobe Stock, Shutterstock, and Freepik microstock submission.
 * Zero external dependencies, pure mathematical coordinate transformations.
 */

export type Matrix = [number, number, number, number, number, number];
export const IDENTITY_MATRIX: Matrix = [1.0, 0.0, 0.0, 1.0, 0.0, 0.0];

const TRANSFORM_REGEX = /([A-Za-z]+)\s*\(([^)]*)\)/g;
const NUMBER_TOKEN_REGEX = /[-+]?(?:\d*\.\d+|\d+)(?:[eE][-+]?\d+)?/g;

export function matrixMultiply(left: Matrix, right: Matrix): Matrix {
  const [a, b, c, d, e, f] = left;
  const [g, h, i, j, k, l] = right;
  return [
    a * g + c * h,
    b * g + d * h,
    a * i + c * j,
    b * i + d * j,
    a * k + c * l + e,
    b * k + d * l + f,
  ];
}

export function matrixPoint(matrix: Matrix, x: number, y: number): [number, number] {
  const [a, b, c, d, e, f] = matrix;
  return [a * x + c * y + e, b * x + d * y + f];
}

export function parseTransform(transformStr: string): Matrix {
  let result: Matrix = [...IDENTITY_MATRIX];
  let match: RegExpExecArray | null;
  TRANSFORM_REGEX.lastIndex = 0;

  while ((match = TRANSFORM_REGEX.exec(transformStr)) !== null) {
    const name = match[1].toLowerCase();
    const values = (match[2].match(NUMBER_TOKEN_REGEX) || []).map(Number);
    let local: Matrix;

    if (name === 'matrix' && values.length === 6) {
      local = [values[0], values[1], values[2], values[3], values[4], values[5]];
    } else if (name === 'translate' && (values.length === 1 || values.length === 2)) {
      local = [1.0, 0.0, 0.0, 1.0, values[0], values.length === 2 ? values[1] : 0.0];
    } else if (name === 'scale' && (values.length === 1 || values.length === 2)) {
      local = [values[0], 0.0, 0.0, values[values.length - 1], 0.0, 0.0];
    } else if (name === 'rotate' && (values.length === 1 || values.length === 3)) {
      const radians = (values[0] * Math.PI) / 180.0;
      const cos = Math.cos(radians);
      const sin = Math.sin(radians);
      const rotation: Matrix = [cos, sin, -sin, cos, 0.0, 0.0];
      if (values.length === 3) {
        const cx = values[1];
        const cy = values[2];
        const translateToOrigin: Matrix = [1.0, 0.0, 0.0, 1.0, -cx, -cy];
        const translateBack: Matrix = [1.0, 0.0, 0.0, 1.0, cx, cy];
        local = matrixMultiply(matrixMultiply(translateBack, rotation), translateToOrigin);
      } else {
        local = rotation;
      }
    } else {
      continue;
    }
    result = matrixMultiply(result, local);
  }
  return result;
}

export interface ValidationResult {
  valid: boolean;
  hasVectorPaths: boolean;
  rasterArtifactsDetected: string[];
  message: string;
}

export class ZeroBitmapValidator {
  static readonly FORBIDDEN_RASTER_PATTERNS = [
    /\bimage\b/i,
    /\bcolorimage\b/i,
    /\bimagemask\b/i,
    /\/DCTDecode/i,
    /\/FlateDecode/i,
    /\/CCITTFaxDecode/i,
    /\/BitsPerComponent/i,
    /\/Indexed/i,
  ];

  static readonly REQUIRED_VECTOR_PATTERNS = [
    /\b(moveto|m)\b/i,
    /\b(lineto|curveto|c|l)\b/i,
    /\b(fill|eofill|f)\b/i,
  ];

  static validateContent(content: string): ValidationResult {
    const foundRasterIssues: string[] = [];
    for (const pattern of this.FORBIDDEN_RASTER_PATTERNS) {
      if (pattern.test(content)) {
        foundRasterIssues.push(pattern.source);
      }
    }

    const hasVectorPaths = this.REQUIRED_VECTOR_PATTERNS.every((pat) => pat.test(content));
    const isValid = foundRasterIssues.length === 0 && hasVectorPaths;

    return {
      valid: isValid,
      hasVectorPaths,
      rasterArtifactsDetected: foundRasterIssues,
      message: isValid
        ? 'Valid Pure Vector EPS 10'
        : `Zero-Bitmap Validation Failed: Detected ${foundRasterIssues.join(', ')}`,
    };
  }

  static validateEps(epsContent: string): boolean {
    const forbidden = [
      'colorimage',
      'image',
      'imagemask',
      'setpattern',
      'shfill',
      'ASCIIHexDecode',
      'ASCII85Decode',
      'RunLengthDecode',
      'FlateDecode',
      'DCTDecode',
    ];
    for (const kw of forbidden) {
      if (epsContent.includes(kw)) {
        return false;
      }
    }
    return true;
  }

  static validateSvg(svgContent: string): {
    noBitmaps: boolean;
    hasText: boolean;
    totalNodes: number;
    totalPaths: number;
  } {
    const hasImageTag = /<image\b/i.test(svgContent);
    const hasBase64 = /data:image\//i.test(svgContent);
    const hasText = /<text\b|<tspan\b/i.test(svgContent);

    const pathMatches = svgContent.match(/<path\b/gi) || [];
    const commandMatches = svgContent.match(/[MmLlHhVvCcSsQqTtAaZz]/g) || [];

    return {
      noBitmaps: !hasImageTag && !hasBase64,
      hasText,
      totalNodes: commandMatches.length,
      totalPaths: pathMatches.length,
    };
  }
}

export class ClientEps10Exporter {
  targetArtboardSize: number;

  constructor(targetArtboardSize: number = 4000) {
    this.targetArtboardSize = targetArtboardSize;
  }

  static svgToEps10(svgContent: string, options?: { targetDimension?: number }): string {
    return new ClientEps10Exporter(options?.targetDimension || 4000).svgToEps10(svgContent);
  }

  parseColor(colorStr: string): [number, number, number] {
    const clean = colorStr.trim();
    if (clean.startsWith('#')) {
      let hexVal = clean.replace(/^#/, '');
      if (hexVal.length === 3) {
        hexVal = hexVal.split('').map((c) => c + c).join('');
      }
      if (hexVal.length >= 6) {
        const r = parseInt(hexVal.substring(0, 2), 16) / 255.0;
        const g = parseInt(hexVal.substring(2, 4), 16) / 255.0;
        const b = parseInt(hexVal.substring(4, 6), 16) / 255.0;
        return [
          Number(r.toFixed(4)),
          Number(g.toFixed(4)),
          Number(b.toFixed(4)),
        ];
      }
    } else if (clean.startsWith('rgb')) {
      const nums = clean.match(/\d+/g);
      if (nums && nums.length >= 3) {
        return [
          Number((parseInt(nums[0], 10) / 255.0).toFixed(4)),
          Number((parseInt(nums[1], 10) / 255.0).toFixed(4)),
          Number((parseInt(nums[2], 10) / 255.0).toFixed(4)),
        ];
      }
    }
    return [0.0, 0.0, 0.0];
  }

  parseSvgViewBox(svgContent: string): [number, number, number, number] {
    const vbMatch = svgContent.match(/viewBox=["']([-\d.\s]+)["']/i);
    if (vbMatch) {
      const parts = vbMatch[1].trim().split(/\s+/).map(Number);
      if (parts.length === 4 && !parts.some(isNaN)) {
        return [parts[0], parts[1], parts[2], parts[3]];
      }
    }
    const wMatch = svgContent.match(/width=["']([\d.]+)["']/i);
    const hMatch = svgContent.match(/height=["']([\d.]+)["']/i);
    const w = wMatch ? Number(wMatch[1]) : 1000.0;
    const h = hMatch ? Number(hMatch[1]) : 1000.0;
    return [0.0, 0.0, w, h];
  }

  private pathToPostScript(
    pathData: string,
    matrix: Matrix,
    minX: number,
    minY: number,
    scale: number,
    targetH: number
  ): string[] {
    const tokens = pathData.match(/[A-Za-z]|[-+]?(?:\d*\.\d+|\d+)(?:[eE][-+]?\d+)?/g) || [];
    const arity: Record<string, number> = { M: 2, L: 2, H: 1, V: 1, C: 6, Q: 4 };
    const lines: string[] = [];
    let index = 0;
    let command = '';
    let current: [number, number] = [0.0, 0.0];
    let start: [number, number] = [0.0, 0.0];
    let subpathOpen = false;
    const dist = (a: [number, number], b: [number, number]) => Math.hypot(a[0] - b[0], a[1] - b[1]);

    const postscript = (point: [number, number]): [number, number] => {
      const [x, y] = matrixPoint(matrix, point[0], point[1]);
      return [(x - minX) * scale, targetH - (y - minY) * scale];
    };

    while (index < tokens.length) {
      const token = tokens[index];
      if (/^[A-Za-z]$/.test(token)) {
        command = token;
        index++;
        if (command.toUpperCase() === 'Z') {
          if (lines.length > 0 && lines[lines.length - 1] !== 'cp') {
            lines.push('cp');
          }
          subpathOpen = false;
          current = start;
          command = '';
          continue;
        }
        if (!(command.toUpperCase() in arity)) {
          throw new Error(`Unsupported SVG path command for EPS export: ${command}`);
        }
      }

      const upper = command.toUpperCase();
      const numArgs = arity[upper];
      if (!numArgs || index + numArgs > tokens.length) {
        break;
      }

      const values: number[] = [];
      let isMalformed = false;
      for (let offset = 0; offset < numArgs; offset++) {
        const valToken = tokens[index + offset];
        if (/^[A-Za-z]$/.test(valToken)) {
          isMalformed = true;
          break;
        }
        values.push(Number(valToken));
      }
      if (isMalformed) break;
      index += numArgs;

      const isRelative = command === command.toLowerCase();
      const [ox, oy] = current;

      if (upper === 'M' || upper === 'L') {
        const point: [number, number] = isRelative
          ? [values[0] + ox, values[1] + oy]
          : [values[0], values[1]];
        if (upper === 'M') {
          if (subpathOpen && lines.length > 0 && lines[lines.length - 1] !== 'cp') {
            lines.push('cp');
          }
          subpathOpen = false;
          start = point;
          current = point;
          const [x, y] = postscript(point);
          lines.push(`${x.toFixed(2)} ${y.toFixed(2)} m`);
          command = isRelative ? 'l' : 'L';
        } else {
          if (dist(current, point) < 1e-4) continue;
          current = point;
          subpathOpen = true;
          const [x, y] = postscript(point);
          lines.push(`${x.toFixed(2)} ${y.toFixed(2)} l`);
        }
      } else if (upper === 'H') {
        const point: [number, number] = [isRelative ? ox + values[0] : values[0], oy];
        if (dist(current, point) < 1e-4) continue;
        current = point;
        subpathOpen = true;
        const [x, y] = postscript(current);
        lines.push(`${x.toFixed(2)} ${y.toFixed(2)} l`);
      } else if (upper === 'V') {
        const point: [number, number] = [ox, isRelative ? oy + values[0] : values[0]];
        if (dist(current, point) < 1e-4) continue;
        current = point;
        subpathOpen = true;
        const [x, y] = postscript(current);
        lines.push(`${x.toFixed(2)} ${y.toFixed(2)} l`);
      } else if (upper === 'C') {
        let points: [number, number][] = [
          [values[0], values[1]],
          [values[2], values[3]],
          [values[4], values[5]],
        ];
        if (isRelative) {
          points = points.map(([px, py]) => [px + ox, py + oy]);
        }
        if (
          dist(current, points[2]) < 1e-4 &&
          dist(current, points[0]) < 1e-4 &&
          dist(current, points[1]) < 1e-4
        ) {
          continue;
        }
        current = points[2];
        subpathOpen = true;
        const converted = points.map(postscript);
        const argsStr = converted.map(([cx, cy]) => `${cx.toFixed(2)} ${cy.toFixed(2)}`).join(' ');
        lines.push(`${argsStr} c`);
      } else if (upper === 'Q') {
        const control: [number, number] = isRelative
          ? [values[0] + ox, values[1] + oy]
          : [values[0], values[1]];
        const endpoint: [number, number] = isRelative
          ? [values[2] + ox, values[3] + oy]
          : [values[2], values[3]];
        if (dist(current, endpoint) < 1e-4 && dist(current, control) < 1e-4) {
          continue;
        }
        const cubic1: [number, number] = [
          ox + ((control[0] - ox) * 2.0) / 3.0,
          oy + ((control[1] - oy) * 2.0) / 3.0,
        ];
        const cubic2: [number, number] = [
          endpoint[0] + ((control[0] - endpoint[0]) * 2.0) / 3.0,
          endpoint[1] + ((control[1] - endpoint[1]) * 2.0) / 3.0,
        ];
        current = endpoint;
        subpathOpen = true;
        const converted = [cubic1, cubic2, endpoint].map(postscript);
        const argsStr = converted.map(([cx, cy]) => `${cx.toFixed(2)} ${cy.toFixed(2)}`).join(' ');
        lines.push(`${argsStr} c`);
      }
    }
    if (subpathOpen && lines.length > 0 && lines[lines.length - 1] !== 'cp') {
      lines.push('cp');
    }
    return lines;
  }

  svgToEps10(svgContent: string, targetArtboard?: number): string {
    const artboardSize = targetArtboard || this.targetArtboardSize;
    const [minX, minY, origW, origH] = this.parseSvgViewBox(svgContent);

    const safeW = origW <= 0 ? 1000.0 : origW;
    const safeH = origH <= 0 ? 1000.0 : origH;

    const scaleX = artboardSize / safeW;
    const scaleY = artboardSize / safeH;
    const scale = Math.min(scaleX, scaleY);

    const targetW = Math.round(safeW * scale);
    const targetH = Math.round(safeH * scale);

    const psLines: string[] = [
      '%!PS-Adobe-3.0 EPSF-3.0',
      '%%Creator: Vectria Studio (Adobe Illustrator Compatible EPS 10)',
      '%%Title: Vectria Vector Output',
      `%%BoundingBox: 0 0 ${Math.floor(targetW)} ${Math.floor(targetH)}`,
      `%%HiResBoundingBox: 0.0000 0.0000 ${targetW.toFixed(4)} ${targetH.toFixed(4)}`,
      `%%CropBox: 0 0 ${Math.floor(targetW)} ${Math.floor(targetH)}`,
      '%%LanguageLevel: 2',
      '%%DocumentData: Clean7Bit',
      '%%Pages: 1',
      '%%EndComments',
      '%%BeginProlog',
      '/m {moveto} bind def',
      '/l {lineto} bind def',
      '/c {curveto} bind def',
      '/cp {closepath} bind def',
      '/f {eofill} bind def',
      '/rgb {setrgbcolor} bind def',
      '%%EndProlog',
      '%%Page: 1 1',
      'gsave',
    ];

    // Simple DOM-based or Regex-based SVG path extractor
    const pathRegex = /<path\b([^>]*)\/?>/gi;
    let match: RegExpExecArray | null;

    while ((match = pathRegex.exec(svgContent)) !== null) {
      const attrs = match[1];
      const dMatch = attrs.match(/\bd=["']([^"']+)["']/i);
      if (!dMatch) continue;
      const dStr = dMatch[1].trim();
      if (!dStr) continue;

      let fill = '#000000';
      const fillMatch = attrs.match(/\bfill=["']([^"']+)["']/i);
      const styleMatch = attrs.match(/\bstyle=["']([^"']+)["']/i);
      if (fillMatch) {
        fill = fillMatch[1].trim();
      } else if (styleMatch) {
        const styleFill = styleMatch[1].match(/(?:^|;)\s*fill\s*:\s*([^;]+)/i);
        if (styleFill) {
          fill = styleFill[1].trim();
        }
      }

      if (fill.toLowerCase() === 'none' || fill.toLowerCase() === 'transparent') {
        continue;
      }

      let matrix: Matrix = [...IDENTITY_MATRIX];
      const transformMatch = attrs.match(/\btransform=["']([^"']+)["']/i);
      if (transformMatch) {
        matrix = parseTransform(transformMatch[1]);
      }

      const [r, g, b] = this.parseColor(fill);
      psLines.push(`\n${r.toFixed(3)} ${g.toFixed(3)} ${b.toFixed(3)} rgb`);
      psLines.push('newpath');
      try {
        const commands = this.pathToPostScript(dStr, matrix, minX, minY, scale, targetH);
        psLines.push(...commands);
        psLines.push('f');
      } catch (err) {
        // Skip malformed individual path gracefully
      }
    }

    psLines.push('grestore', 'showpage', '%%Trailer', '%%EOF');
    return psLines.join('\n');
  }

  convertSvgToEps10(svgContent: string, targetArtboard?: number): string {
    return this.svgToEps10(svgContent, targetArtboard);
  }
}
